import { anotherGateIsUp } from "@/lib/daily-brief";

/** Text-like input types a person can actually type characters into —
 *  excludes checkbox/radio/button/submit/range/color/file, which `focus()`
 *  on a dialog is fine to interrupt. */
const TEXT_ENTRY_INPUT_TYPES = new Set([
  "text", "search", "email", "tel", "url", "password", "number", "date",
  "datetime-local", "month", "time", "week",
]);

/**
 * True while the person is actively focused in a field they can type
 * into — any `<input>` of a text-like type, a `<textarea>`, or a
 * `contenteditable` element.
 *
 * CAMP search-flicker fix (2026-09-28) — "when I search in the mobile app —
 * any search option, anywhere — it flickers, doesn't stay there, the
 * keyboard goes away." `useDialogA11y`'s focus trap (hooks/use-dialog-a11y.ts)
 * correctly moves focus into a dialog the instant it opens, for anyone whose
 * focus was outside it — which is the right behaviour for a dialog the
 * person asked to open, but the "Remember this device?" sheet and the soft
 * PIN-setup nudge open themselves, unasked, on a 2s / 4s timer after the
 * shell mounts. That timer has no idea a person is mid-keystroke in the
 * header search, a module list's search box, or a recipient picker — the
 * search input is not inside any dialog, so the trap's own "is focus already
 * inside me" check is false, and it yanks focus onto the sheet's first
 * button. The OS keyboard closes because the newly-focused element isn't a
 * text field, and the search box reads as having "flickered" away.
 */
export function isTypingInAField(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  // `isContentEditable` isn't implemented in jsdom (always false there), so
  // this also checks the attribute directly for the same truth in tests.
  const editableAttr = el.getAttribute("contenteditable");
  if (el.isContentEditable || editableAttr === "true" || editableAttr === "") return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    return TEXT_ENTRY_INPUT_TYPES.has(el.type);
  }
  return false;
}

/**
 * Run `show` after `delayMs`, but only once no other dialog / first-run gate
 * is open, AND the person isn't actively typing into a field anywhere on the
 * page — re-checking every 3s (500ms for the typing check, so it opens the
 * moment they pause rather than waiting out the full retry window). The soft
 * device-trust and PIN nudges used to fire on a fixed 2s / 4s timer, so a
 * teacher who opened "Apply leave" straight away had the PIN sheet land on
 * top of the half-filled form, its backdrop swallowing every tap (E2E
 * sweep) — and, separately, anyone typing into ANY search box in that same
 * window had it steal the keyboard mid-search (see isTypingInAField above).
 * Returns the cleanup.
 */
export function whenNoOtherDialog(delayMs: number, show: () => void): () => void {
  let t: ReturnType<typeof setTimeout>;
  const attempt = () => {
    // Any open dialog counts, aria-modal or not (Radix dialogs such as
    // "Apply leave" don't all set it) — this also queues the PIN nudge behind
    // the device-trust sheet instead of stacking them.
    const dialogOpen = Array.from(document.querySelectorAll("[role='dialog']"))
      .some((el) => !el.closest("[data-daily-brief]"));
    if (dialogOpen || anotherGateIsUp()) { t = setTimeout(attempt, 3000); return; }
    if (isTypingInAField()) { t = setTimeout(attempt, 500); return; }
    show();
  };
  t = setTimeout(attempt, delayMs);
  return () => clearTimeout(t);
}
